import {
  clearCanvasGpuTurn,
  waitForCanvasGpuTurn,
} from "./canvas-gpu-lock";
import {
  destroyWebGpuCanvas,
  requestWebGpuCanvas,
  resizeWebGpuCanvas,
  watchCanvasResize,
} from "./webgpu-canvas";

export async function initWebGpuStage(
  canvas: HTMLCanvasElement,
  onStatus: (detail: string) => void,
): Promise<() => void> {
  const releaseTurn = await waitForCanvasGpuTurn(canvas);

  const finishEmpty = (): (() => void) => {
    clearCanvasGpuTurn(canvas, releaseTurn);
    return () => undefined;
  };

  const init = await requestWebGpuCanvas(canvas);
  if (!init.ok) {
    const webGl2 = document.createElement("canvas").getContext("webgl2") !== null;
    onStatus(webGl2 ? `WebGL2 enabled. WebGPU unavailable: ${init.reason}` : init.reason);
    return finishEmpty();
  }

  const handle = init.handle;
  onStatus("WebGPU enabled");

  const unwatchResize = watchCanvasResize(canvas, () => {
    resizeWebGpuCanvas(canvas, handle);
  });

  const onGpuError: EventListener = (event): void => {
    const gpuEvent = event as GPUUncapturedErrorEvent;
    onStatus(`GPU error: ${gpuEvent.error.message}`);
  };
  handle.device.addEventListener("uncapturederror", onGpuError);

  return () => {
    unwatchResize();
    handle.device.removeEventListener("uncapturederror", onGpuError);
    void destroyWebGpuCanvas(handle).finally(() => {
      clearCanvasGpuTurn(canvas, releaseTurn);
    });
  };
}
